import * as React from 'react';

import Icon from '~/components/icon';
import { useLoaderContext } from '~/providers/loader-context';
import { ModalView, useModalContext } from '~/providers/modal-context';
import { useMyOCContext } from '~/providers/myoc-context';
import { CookieService } from '~/services/actions/cookie-actions';
import TrackingActions, {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS } from
'~/services/actions/tracking-actions';
// import PremiumProductForm from "../premium-product-form";
// import PremiumPaymentVerification from "../premium-payment-verification";
// import PremiumPaymentFailure from "../premium-payment-failure";
// import PremiumPaymentSuccess from "../premium-payment-success";
import { AccountPersonalisation } from '~/shared';
import { type MyOCData } from '~/types/myoc';
import {
  comp,
  gtmTrackingDeclinePremium,
  helpClickHandler,
  removeModalSearchParamsFromUrl } from
'~/utils/us-utils';

import useMedia from '../../hooks/use-media';
import {
  myOCLoginModals,
  myOCLoginModalsEmbeddedInPremiumModals } from
'../../stores/constants';

import styles from './style/premium-modal.module.css';

export interface IProps {
  children?: React.ReactNode;
  hideHeader?: boolean;
  initView?: ModalView;
}

export const PremiumModal: React.FC<IProps> = ({
  children,
  hideHeader,
  initView
}) => {
  const { isMobile } = useMedia();
  const cookieActions = React.useMemo(() => new CookieService(), []);
  const { baseUrlWithProtocolAndRepub, currentPath } = useLoaderContext();

  const { myocData } = useMyOCContext();
  const { isLoggedIn, myOCUser } = myocData;
  const isPremium = myOCUser?.isPremium;

  const [
  {
    // prettier-ignore
    loading,
    ocAuthReady,
    refreshPage,
    searchString,
    showModal,
    view
  },
  dispatch] =
  useModalContext();

  const getQueryParams = React.useCallback(() => {
    const search =
    searchString || typeof window !== 'undefined' ?
    window.location.search.substring(1) :
    currentPath;
    const params = new URLSearchParams(search);

    return params;
  }, [searchString, currentPath]);

  const getModalParams = React.useCallback(() => {
    const params = getQueryParams();
    const status = params?.get('status');

    const hasSignupParam = params?.has('signup');
    const hasLoginParam = params?.has('login');
    const hasOCPremiumParam = params?.has('ocpremium');
    const hasAccountPersonalisation = params?.has('accountpersonalisation');

    return {
      showSignupModal: hasSignupParam,
      showLoginModal: hasLoginParam,
      showMyOCModal: hasOCPremiumParam,
      showPaymentVerification: hasOCPremiumParam && status === 'verification',
      showPaymentSuccess: hasOCPremiumParam && status === 'success',
      showPaymentFailure: hasOCPremiumParam && status === 'failure',
      showAccountPersonalisation: hasAccountPersonalisation
    };
  }, [getQueryParams]);

  const trackingActions = React.useMemo(() => new TrackingActions(), []);

  // Set ocAuthReady from window
  React.useEffect(() => {
    if (!dispatch) return;

    window.addEventListener('OC:ocAuthReady', () => {
      dispatch({ type: 'SET_OC_AUTH_READY' });
    });
  }, [dispatch]);

  // Set view, showModal and loading
  React.useEffect(() => {
    const {
      showAccountPersonalisation,
      showLoginModal,
      showMyOCModal,
      showPaymentFailure,
      showPaymentSuccess,
      showPaymentVerification,
      showSignupModal
    } = getModalParams();

    const modalView = (() => {
      switch (true) {
        case showSignupModal && !isLoggedIn:
          return ModalView.SignupForm;

        case showLoginModal && !isLoggedIn:
          return ModalView.LoginForm;

        case showPaymentVerification && !isPremium:
          return ModalView.PremiumPaymentVerification;

        case showPaymentSuccess && !isPremium:
          return ModalView.PremiumPaymentSuccess;

        case showPaymentFailure && !isPremium:
          return ModalView.PremiumPaymentFailure;

        case showMyOCModal && !isPremium:
          return ModalView.PremiumProductForm;

        case showAccountPersonalisation:
          return ModalView.AccountPersonalisation;

        default:
          return view || initView;
      }
    })();

    const canShowView = !!modalView;
    const canShowChildren = typeof children !== 'undefined';
    const canShowProductModal = !!(showMyOCModal && searchString && !isPremium);
    const canDisableLoading = !(
    canShowProductModal ||
    canShowView ||
    canShowChildren);


    dispatch({
      type: 'SET_SHOW_MODAL_VIEW_AND_LOADING',
      payload: {
        view: modalView,
        showModal: canShowView || canShowChildren,
        loading: canDisableLoading
      }
    });
  }, [
  searchString,
  getModalParams,
  view,
  initView,
  isLoggedIn,
  isPremium,
  children,
  dispatch]
  );

  // Reload the page
  React.useEffect(() => {
    if (!refreshPage) return;

    window.location.reload();
  }, [refreshPage]);

  // Subscribe to OC:LoggedIn event
  React.useEffect(() => {
    if (!dispatch || !cookieActions || !currentPath) return;

    const openPersonalisationModalOrRefreshPageOnLogin = (e: Event): void => {
      const myoc: MyOCData = (e as CustomEvent)?.detail?.myoc;

      if (!myoc?.isLoggedIn) return;

      const myocPersonalisationCookie = cookieActions.
      select('myoc-personalisation').
      getString();
      const userHasViewedAccountPersonalisation =
      !!myocPersonalisationCookie || !!myoc.myOCUser?.personalisation;

      const isOCPlusPage = currentPath.startsWith('/plus');
      const isOCSubsLandingPage = currentPath.startsWith('/subscribe');

      const shouldRenderAccountPersonalisationModal =
      !userHasViewedAccountPersonalisation &&
      !isOCPlusPage &&
      !isOCSubsLandingPage;

      switch (true) {
        case shouldRenderAccountPersonalisationModal:
          dispatch({
            type: 'SET_VIEW',
            payload: {
              view: 'AccountPersonalisation'
            }
          });
          break;

        default:
          // NOTE: we *might* want to redirect to homepage here, as we did with
          // the old logic found under myoc-controller.handleClose helper, i.e.
          // in circumstances where policyupdaterequired is in query params

          dispatch({ type: 'REFRESH_PAGE' });
          break;
      }
    };

    window.addEventListener(
      'OC:LoggedIn',
      openPersonalisationModalOrRefreshPageOnLogin
    );

    return () => {
      window.removeEventListener(
        'OC:LoggedIn',
        openPersonalisationModalOrRefreshPageOnLogin
      );
    };
  }, [dispatch, cookieActions, currentPath]);

  // Invoke window.ocAuth.openAtView methods
  React.useEffect(() => {
    removeModalSearchParamsFromUrl();

    if (!ocAuthReady) return;

    switch (view) {
      case ModalView.LoginForm:
        window.ocAuth.openAtView('LOGIN');
        break;

      case ModalView.LoginVerificationForm:
        window.ocAuth.openAtView('LOGIN_FROM_EMAIL_VERIFICATION');
        break;

      case ModalView.SignupForm:
        window.ocAuth.openAtView('SIGN_UP');
        break;

      default:
        break;
    }
  }, [view, ocAuthReady]);

  // NOTE: we require the FE flag - showModal - in order that
  // we can conditionally render modals according to user events

  const hasChildren = !!children;
  const viewHasModal = (view || '') in ModalView;

  const viewIsMyOCLoginModal = myOCLoginModals.includes(view as string);
  const viewIsMyOCLoginModalEmbeddedInPremiumModal =
  myOCLoginModalsEmbeddedInPremiumModals.includes(view as string);

  const shouldHideModal = (() => {
    switch (true) {
      // simple cases
      case loading:
      case !showModal:
      case !viewHasModal && !hasChildren:
        return true;

      // complex cases
      case !ocAuthReady && viewIsMyOCLoginModal:
        return true;

      default:
        return false;
    }
  })();

  const shouldHideHeader = (() => {
    switch (true) {
      case hideHeader:
      case viewIsMyOCLoginModal:
      case viewIsMyOCLoginModalEmbeddedInPremiumModal && isMobile:
        return true;

      default:
        return false;
    }
  })();

  const closeModal = (): void => {
    removeModalSearchParamsFromUrl();
    window.ocAuth.openAtView('Close');
    dispatch({ type: 'HIDE_MODAL' });
  };

  const closeModalAndRefreshPage = (): void => {
    removeModalSearchParamsFromUrl();
    window.ocAuth.openAtView('Close');
    dispatch({ type: 'HIDE_MODAL_AND_REFRESH_PAGE' });
  };

  // const closeModalAndRedirectToPicksHub = () => {
  // 	closeModal();
  // 	const picksHubUrl = window.location.origin + '/us/picks-parlays';
  // 	window.location.href = picksHubUrl;
  // };

  // Add modal helper methods to window
  React.useEffect(() => {
    if (!dispatch) return;

    window.openModalAtView = (modalView: ModalView): void => {
      dispatch({
        type: 'SET_VIEW',
        payload: {
          view: modalView
        }
      });
    };

    window.closeMyOCPremiumModal = (): void => {
      removeModalSearchParamsFromUrl();
      dispatch({ type: 'HIDE_MODAL' });
    };

    window.closeMyOCLoginModal = (): void => {
      removeModalSearchParamsFromUrl();
      window.ocAuth.openAtView('Close');
    };

    window.closeAllModals = (): void => {
      removeModalSearchParamsFromUrl();
      window.ocAuth.openAtView('Close');
      dispatch({ type: 'HIDE_MODAL' });
    };
  }, [dispatch]);

  const gtmTrackingPremiumFAQIcon = (): void => {
    trackingActions.eventTracking({
      event: TRACKING_EVENTS.MYOC_FAQ_ICON,
      eventAction: TRACKING_ACTIONS.myoc_faq_icon,
      eventCategory: TRACKING_CATEGORIES.PREMIUM,
      eventLabel: 'faq_icon'
    });
  };

  const gtmTrackingPremiumPaymentFailure = (label: string): void => {
    trackingActions.eventTracking({
      event: TRACKING_EVENTS.SUBSCRIPTION_CONTACT_US,
      eventAction: TRACKING_ACTIONS.subscription_contact_us,
      eventCategory: TRACKING_CATEGORIES.PREMIUM,
      eventLabel: label
    });
  };

  const renderHeader = (): JSX.Element | null => {
    if (shouldHideHeader) return null;

    const headerCloseIconClickHandler = (): void => {
      switch (view) {
        case ModalView.PremiumProductForm:
          gtmTrackingDeclinePremium();
          closeModal();
          break;

        case ModalView.PremiumPaymentSuccess:
          closeModalAndRefreshPage();
          break;

        case ModalView.PremiumPaymentFailure:
          gtmTrackingPremiumPaymentFailure('close window');
          closeModal();
          break;

        // NOTE: following case is intentionally empty as
        // there are no UI elements to close the modal
        case ModalView.PremiumPaymentVerification:
          break;

        default:
          closeModal();
          break;
      }
    };

    const renderHelpIcon = (): JSX.Element => {
      if (view !== ModalView.PremiumProductForm) return <span />;

      const handleHelpClick = () => {
        gtmTrackingPremiumFAQIcon();
        helpClickHandler(baseUrlWithProtocolAndRepub);
      };

      return (
        <span
          className={styles.helpIconWrapper}
          data-testid="help-icon"
          onClick={handleHelpClick}>

					<Icon className={styles.helpIcon} icon="PremiumHelp" />
				</span>);

    };

    const renderCloseIcon = (): JSX.Element => {
      if (view === ModalView.PremiumPaymentVerification) return <span />;

      return (
        <span
          className={styles.closeIconWrapper}
          data-testid="close-modal"
          onClick={headerCloseIconClickHandler}>

					<Icon
            className={styles.closeIcon}
            height={24}
            icon="Close"
            width={26} />

				</span>);

    };

    return (
      <div className={styles.modalHeader} data-testid="modal-header">
				{renderHelpIcon()}
				{renderCloseIcon()}
			</div>);

  };

  const renderViewOrChildren = ():
  JSX.Element |
  JSX.Element[] |
  null |
  undefined => {
    // 1. Render Views
    switch (view) {
      case ModalView.PremiumProductForm:
        return <div>PremiumProductForm</div>;
      // return <PremiumProductForm closeModal={closeModal} />;

      case ModalView.PremiumPaymentVerification:
        return <div>PremiumPaymentVerification</div>;
      // return <PremiumPaymentVerification />;

      case ModalView.PremiumPaymentSuccess:
        return <div>PremiumPaymentSuccess</div>;
      // return (
      //   <PremiumPaymentSuccess
      //     closeModalAndRedirectToPicksHub={closeModalAndRedirectToPicksHub}
      //   />
      // );

      case ModalView.PremiumPaymentFailure:
        return <div>PremiumPaymentFailure</div>;
      // return (
      //   <PremiumPaymentFailure
      //     closeModal={closeModal}
      //     gtmTrackingPremiumPaymentFailure={gtmTrackingPremiumPaymentFailure}
      //   />
      // );

      case ModalView.AccountPersonalisation:
        return (
          <AccountPersonalisation
            baseUrl={baseUrlWithProtocolAndRepub}
            closeModal={closeModal}
            shouldRenderHeadless={true} />);



      default:
        break;
    }

    // 2. Render Children
    return React.Children.map(children, (child) => {
      const item = child as React.ReactElement;

      // Return item if regular DOM element
      if (typeof item.type === 'string') {
        return item;
      }

      // Return compound component
      return React.cloneElement(item, {
        closeModal,
        closeModalAndRefreshPage
      });
    });
  };

  const renderLoginModal = (): JSX.Element => {
    return <div id="myoc-login-modal"></div>;
  };

  const isFullScreen = viewIsMyOCLoginModalEmbeddedInPremiumModal;

  return (
    <div
      className={comp(
        styles.modalBackground,
        shouldHideModal ? styles.hidden : ''
      )}>

			<div
        className={comp(
          styles.modalContainer,
          isFullScreen ? styles.fullScreen : ''
        )}
        data-testid="myoc-modal-container">

				{renderHeader()}
				{renderViewOrChildren()}
				{renderLoginModal()}
			</div>
		</div>);

};